import React, { useState, useEffect } from "react";
import { X, Download, Eye } from "lucide-react";
import { getFileExtensionByMime } from "@/lib/utils";

// Document Viewer Modal Component
const DocumentViewerModal = ({
  isOpen,
  onClose,
  documentData,
  documentName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Check if the document is an image or PDF
  const isImage = documentData?.contentType?.startsWith("image/");
  const isPdf = documentData?.contentType === "application/pdf";

  useEffect(() => {
    if (isOpen && (isImage || isPdf) && documentData?.data) {
      try {
        // Convert base64 to binary
        const base64Data = documentData.data;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob from binary data
        const blob = new Blob([bytes], { type: documentData.contentType });
        const blobUrl = URL.createObjectURL(blob);
        
        if (isImage) {
          setImageUrl(blobUrl);
        } else {
          setPdfUrl(blobUrl);
        }

        // Cleanup function
        return () => {
          URL.revokeObjectURL(blobUrl);
        };
      } catch (err) {
        console.error("Error creating document URL:", err);
        setError("Failed to load document preview");
      }
    }
  }, [isOpen, isImage, isPdf, documentData]);

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
        setImageUrl(null);
      }
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }
  }, [isOpen, imageUrl, pdfUrl]);

  if (!isOpen) return null;

  const handleViewDocument = async () => {
    if (!documentData?.data || !documentData?.contentType) {
      setError("Document data is not available");
      return;
    }

    // If it's an image, we're already showing it in the modal
    if (isImage) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert base64 to binary
      const base64Data = documentData.data;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob from binary data
      const blob = new Blob([bytes], { type: documentData.contentType });
      const blobUrl = URL.createObjectURL(blob);

      // Open in new tab
      window.open(blobUrl, "_blank");

      // Clean up the blob URL after opening
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (err) {
      console.error("Error viewing document:", err);
      setError("Failed to view document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async () => {
    if (!documentData?.data || !documentData?.contentType) {
      setError("Document data is not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert base64 to binary
      const base64Data = documentData.data;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob from binary data
      const blob = new Blob([bytes], { type: documentData.contentType });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename based on document type and content type
      const extension = getFileExtensionByMime(documentData.contentType);
      link.download = `${documentName.replace(/\s+/g, "_")}${extension}`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading document:", err);
      setError("Failed to download document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-lg shadow-xl mx-4 transition-all duration-300 ${isImage || isPdf ? "max-w-5xl w-full" : "max-w-md w-full"
          }`}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {documentName}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {/* Document Preview */}
          {(isImage && imageUrl) || (isPdf && pdfUrl) ? (
            <div className="mb-6 flex justify-center border rounded-lg overflow-hidden bg-gray-50">
              {isImage ? (
                <img
                  src={imageUrl}
                  alt={documentName}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={() => setError("Failed to load image")}
                />
              ) : (
                <iframe
                  src={pdfUrl}
                  title={documentName}
                  className="w-full h-[60vh] border-0"
                  onError={() => setError("Failed to load PDF preview")}
                />
              )}
            </div>
          ) : null}

          {/* Document Info */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">
              <strong className="text-gray-900">Type:</strong> {documentData?.contentType || "Unknown"}
            </div>
            {documentData?.uploadedAt && (
              <div className="text-sm text-gray-600 mb-2">
                <strong className="text-gray-900">Uploaded:</strong>{" "}
                {new Date(documentData.uploadedAt).toLocaleString()}
              </div>
            )}
            <div className="text-sm text-gray-600">
              <strong className="text-gray-900">Size:</strong>{" "}
              {documentData?.data
                ? `${Math.round((documentData.data.length * 0.75) / 1024)} KB`
                : "Unknown"}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            {!isImage && !isPdf && (
              <button
                onClick={handleViewDocument}
                disabled={loading || !documentData?.data}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg text-white font-medium transition-colors ${loading || !documentData?.data
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                ) : (
                  <Eye size={16} className="mr-2" />
                )}
                View Document
              </button>
            )}

            <button
              onClick={handleDownloadDocument}
              disabled={loading || !documentData?.data}
              className={`${isImage || isPdf ? "w-full" : "flex-1"
                } flex items-center justify-center px-4 py-2 rounded-lg text-white font-medium transition-colors ${loading || !documentData?.data
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
                }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DocumentViewerModal };
